import { useState } from 'react'
import type { Webhook, WebhookEventType } from '@/api/hooks/use-webhooks'
import { useCreateWebhook, useUpdateWebhook } from '@/api/hooks/use-webhooks'
import { Modal } from '@/components/ui/modal'

const ALL_EVENTS: WebhookEventType[] = [
  'email.queued',
  'email.sent',
  'email.delivered',
  'email.bounced',
  'email.complained',
  'email.opened',
  'email.clicked',
  'email.rejected',
  'email.delayed',
  'email.spam_trap',
]

interface WebhookFormModalProps {
  webhook: Webhook | null
  onClose: () => void
}

export function WebhookFormModal({ webhook, onClose }: WebhookFormModalProps): React.JSX.Element {
  const isEditing = webhook !== null
  const createWebhook = useCreateWebhook()
  const updateWebhook = useUpdateWebhook()

  const [url, setUrl] = useState(webhook?.url ?? '')
  const [events, setEvents] = useState<Set<WebhookEventType>>(new Set(webhook?.events ?? []))
  const [description, setDescription] = useState(webhook?.description ?? '')

  function toggleEvent(event: WebhookEventType): void {
    setEvents((prev) => {
      const next = new Set(prev)
      if (next.has(event)) {
        next.delete(event)
      } else {
        next.add(event)
      }
      return next
    })
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault()
    const eventsArray = Array.from(events)
    if (isEditing) {
      updateWebhook.mutate(
        {
          id: webhook.id,
          data: { url, events: eventsArray, description: description || undefined },
        },
        { onSuccess: onClose },
      )
    } else {
      createWebhook.mutate(
        { url, events: eventsArray, description: description || undefined },
        { onSuccess: onClose },
      )
    }
  }

  const isPending = createWebhook.isPending || updateWebhook.isPending
  const isValid = url.startsWith('https://') && events.size > 0

  return (
    <Modal
      open
      onClose={onClose}
      title={isEditing ? 'Edit Webhook' : 'Add Webhook Endpoint'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="webhook-url" className="block text-sm font-medium text-text-primary">
            URL
          </label>
          <input
            id="webhook-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            required
            placeholder="https://api.example.com/webhooks"
            className="mt-1 w-full rounded-lg border border-surface-3 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
          />
          <p className="mt-1 text-xs text-text-tertiary">Must be an HTTPS URL</p>
        </div>

        <div>
          <span className="block text-sm font-medium text-text-primary">Events</span>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {ALL_EVENTS.map((event) => (
              <label
                key={event}
                className="flex items-center gap-2 rounded-lg border border-surface-3 px-3 py-2 text-sm hover:bg-surface-1"
              >
                <input
                  type="checkbox"
                  checked={events.has(event)}
                  onChange={() => toggleEvent(event)}
                  className="rounded border-surface-3"
                />
                <span className="text-text-primary">{event}</span>
              </label>
            ))}
          </div>
          {events.size === 0 && (
            <p className="mt-1 text-xs text-danger">Select at least one event</p>
          )}
        </div>

        <div>
          <label htmlFor="webhook-desc" className="block text-sm font-medium text-text-primary">
            Description (optional)
          </label>
          <input
            id="webhook-desc"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Slack bounce alerts"
            className="mt-1 w-full rounded-lg border border-surface-3 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-surface-3 px-4 py-2 text-sm hover:bg-surface-1"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isValid || isPending}
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {isPending ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Endpoint'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
